<template lang="pug">
.rhpa-programming
  Modal(v-if="showAccessModal" :header="__('You have already purchased this resource.')" @close="showAccessModal = false")
    p {{__('You can access this program from the Iron Game Programming page in My Account')}}
    RogueButton.link(link="/account/programming" width="full" ) {{__('View My Programs') }}
  Message.ebook.msg-extra-padding(v-if="status === 'active' || status === 'trial'")
    p {{__('You have already purchased this program. Programs can be accessed within your Rogue Account.') }}
    rogue-link.link(to="/account/programming" width="full") {{__('View My Programs')}}
  Message.ebook.msg-extra-padding(v-else-if="status === 'pending_cancellation'" status="warning")
    Icon.left-icon(name="misc/calendar")
    p {{__('Your subscription will expire on {date}. To reactivate, visit the My Programs page.', {date: new Date(purchase.subscription.nextPaymentDate).toLocaleDateString() }) }}
    rogue-link.link(to="/account/programming" width="full") {{__('View My Programs')}}
  Message.ebook.msg-extra-padding(v-else-if="status === 'payment_overdue'" status="error")
    Icon.left-icon(name="alert-error")
    p {{__('There has been an error processing your payment. Please update your credit card to continue receiving access to your program.') }}
    rogue-link.link(to="/account/programming" width="full") {{__('View My Programs')}}
  Message.ebook(v-else-if="status === 'cancelled'" status="warning")
    Icon.left-icon(name="misc/calendar")
    p {{__('Your subscription expired on {date}.', {date: new Date(purchase.subscription.nextPaymentDate).toLocaleDateString() }) }}
    | {{__('To reactivate, use the Add to Cart button below to purchase a new subscription.') }}
</template>
<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import type { Program, Purchase, SubscriptionStatus } from '@/js/pages/account/subroutes/programming/types'
import type { Product } from '@/js/services/RogueTypes'
import { useRogue } from '@/js/services/Rogue'
import { useCustomerStore } from '@/js/stores/customerStore'

const props = defineProps<{
  product: Product,
}>()

const rogue = useRogue()
const customerStore = useCustomerStore()

const status = ref<SubscriptionStatus | null | undefined>(null)
const showAccessModal = ref(false)
const purchase = ref<Purchase | null>(null)

const loggedIn = computed(() => customerStore.loggedIn)

async function checkPrograms() {
  if (!loggedIn.value) {
    status.value = null
  } else {
    const programData = await rogue.fetch<Program[]>('/svc_programming/programs')

    const foundPurchase = programData.find(p => p.sku === props.product.sku)?.purchase
    purchase.value = foundPurchase ?? null
    if (foundPurchase && !foundPurchase.subscription) {
      status.value = 'active'
    } else {
    // Check if the customer is not already subscribed to this program
      status.value = foundPurchase?.subscription?.currentStatus
    }
  }
}

async function validate() {
  // Make sure we have loaded subscription data
  if (status.value === null) {
    await checkPrograms()
  }
  // Only allow add to cart if we aren't already subscribed
  if (status.value && status.value !== 'cancelled') {
    showAccessModal.value = true

    return false
  }

  return true
}

defineExpose({
  validate,
})

watch(loggedIn, async () => {
  await checkPrograms()
})

// the buyRequest is switched out on trial products therefore this is needed to find the new sku in checkPrograms()
watch(
  () => props.product.sku,
  async () => {
    await checkPrograms()
  }
)

onMounted(async () => {
  await checkPrograms()
})
</script>
<style lang="scss" scoped>

.ebook {
  margin-bottom: $space-s;
}

.account-message {
  font-weight: $weight-bold;
}

.divider {
  height: 1px;
  background-color: $color-gray;
  margin: $size-s 0;
}

</style>
